import { PdfPageSpec, kv, rule, t } from './generate-test-pdf';
import { SwProfile, formatDate, formatMoneyPlain } from './star-wars-profile';

export type TemplateVariant = 'standard' | 'b' | 'replacement';

export interface LabTemplateDef {
    id: string;
    filename: string;
    label: string;
    group: string;
    variants: TemplateVariant[];
    build: (profile: SwProfile, variant: TemplateVariant) => PdfPageSpec[];
}

function ssnLast4(p: SwProfile): string {
    return (p.ssnDecrypted || '').replace(/\D/g, '').slice(-4);
}

function ssnFull(p: SwProfile): string {
    const d = (p.ssnDecrypted || '').replace(/\D/g, '');
    if (d.length < 9) {
        return d;
    }
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

function banner(variant: TemplateVariant): PdfPageSpec['items'] {
    if (variant === 'replacement') {
        return [t('REPLACEMENT UPLOAD', 14, 10), t('This file replaces a prior submission.', 10, 14)];
    }
    if (variant === 'b') {
        return [t('LAYOUT VARIANT B', 10, 12)];
    }
    return [];
}

function receiptShell(
    title: string,
    profile: SwProfile,
    variant: TemplateVariant,
    extra: Array<ReturnType<typeof t>>
): PdfPageSpec[] {
    const items = [
        ...banner(variant),
        t(title, 16, 10),
        t(profile.vendor, 12, 8),
        t(profile.city + ', ' + profile.planet, 10, 14),
        rule(),
        kv('Guest', profile.name || ''),
        kv('Alias', profile.alias || ''),
        ...extra,
        kv('Amount', formatMoneyPlain(profile.receiptAmount || 0)),
        kv('Date', formatDate(profile.receiptDate || '')),
        rule(),
        t('Thank you for traveling with the Alliance.', 10, 10)
    ];
    return [{ items }];
}

const payStub: LabTemplateDef = {
    id: 'paystub',
    filename: '01_PayStub.pdf',
    label: '01 Pay Stub',
    group: 'Income',
    variants: ['standard'],
    build: (p) => [
        {
            items: [
                t(p.employer, 14, 8),
                t('EMPLOYEE PAY STATEMENT', 16, 14),
                rule(),
                kv('Employee', p.name || ''),
                kv('First', p.firstName || ''),
                kv('Last', p.lastName || ''),
                kv('Alias', p.alias || ''),
                kv('SSN', 'XXX-XX-' + ssnLast4(p)),
                rule(),
                t('Identity page. Earnings amounts are on page 2 only.', 10, 10)
            ]
        },
        {
            items: [
                t(p.employer + ' - Earnings Detail', 14, 14),
                kv('Employee', p.name || ''),
                kv('Stub start', formatDate(p.incomePayStubStartDate || '')),
                kv('Stub end', formatDate(p.incomePayStubEndDate || '')),
                kv('Stub gross', formatMoneyPlain(p.incomePayStubGross || 0)),
                kv('Net pay', formatMoneyPlain((p.incomePayStubGross || 0) * 0.72)),
                rule(),
                t('Direct deposit to Outer Rim Credit Union', 10, 10),
                t('PAGE-2-ONLY FIELDS: stub gross / stub dates', 9, 8)
            ]
        }
    ]
};

const taxDocument: LabTemplateDef = {
    id: 'tax',
    filename: '02_TaxDocument.pdf',
    label: '02 Tax Document',
    group: 'Income',
    variants: ['standard'],
    build: (p) => [
        {
            items: [
                t('Tax Document Sample (Fictional)', 16, 10),
                t(p.employer, 12, 14),
                rule(),
                kv('Employee name', p.name || ''),
                kv('First', p.firstName || ''),
                kv('Last', p.lastName || ''),
                kv('SSN', ssnFull(p)),
                kv('DOB', formatDate(p.birthDate || '')),
                rule(),
                t('Identity page. Wage amounts continue on page 2 only.', 10, 10)
            ]
        },
        {
            items: [
                t('Tax wage statement - Page 2', 14, 14),
                kv('Employee name', p.name || ''),
                kv('Tax year', String(p.incomeIrsYear || '')),
                kv('IRS gross', formatMoneyPlain(p.incomeIrsYearGross || 0)),
                kv('Federal income tax withheld', formatMoneyPlain((p.incomeIrsYearGross || 0) * 0.12)),
                rule(),
                t('PAGE-2-ONLY FIELDS: tax year / IRS gross', 9, 8)
            ]
        }
    ]
};

const w2Document: LabTemplateDef = {
    id: 'w2',
    filename: '02b_W2.pdf',
    label: '02b W-2',
    group: 'Income',
    variants: ['standard'],
    build: (p) => [
        {
            items: [
                t('Form W-2 Wage and Tax Statement (Fictional)', 16, 10),
                t(p.employer, 12, 14),
                rule(),
                kv('Employee name', p.name || ''),
                kv('SSN', ssnFull(p)),
                kv('Employer', p.employer),
                rule(),
                t('Identity page. Box 1 wages on page 2 only.', 10, 10)
            ]
        },
        {
            items: [
                t('Form W-2 - Page 2', 14, 14),
                kv('Employee name', p.name || ''),
                kv('Tax year', String(p.incomeIrsYear || '')),
                kv('Box 1 Wages', formatMoneyPlain(p.incomeIrsYearGross || 0)),
                kv('IRS gross', formatMoneyPlain(p.incomeIrsYearGross || 0)),
                rule(),
                t('PAGE-2-ONLY FIELDS: tax year / IRS gross', 9, 8)
            ]
        }
    ]
};

const socialSecurityDocument: LabTemplateDef = {
    id: 'social_security',
    filename: '02c_SocialSecurity.pdf',
    label: '02c Social Security',
    group: 'Income',
    variants: ['standard'],
    build: (p) => [
        {
            items: [
                t('Social Security Benefit Statement (Fictional)', 16, 10),
                rule(),
                kv('Beneficiary', p.name || ''),
                kv('SSN', ssnFull(p)),
                kv('Alias', p.alias || ''),
                rule(),
                t('Identity page. Benefit amounts continue on page 2 only.', 10, 10)
            ]
        },
        {
            items: [
                t('Social Security - Benefit detail', 14, 14),
                kv('Beneficiary', p.name || ''),
                kv('Benefit year', String(p.incomeIrsYear || '')),
                kv('Benefit amount', formatMoneyPlain(p.incomeIrsYearGross || 0)),
                rule(),
                t('PAGE-2-ONLY FIELDS: benefit year / benefit amount', 9, 8)
            ]
        }
    ]
};

const lodging: LabTemplateDef = {
    id: 'lodging',
    filename: '01_Lodging_Receipt.pdf',
    label: '01 Lodging Receipt',
    group: 'Travel',
    variants: ['standard', 'b'],
    build: (p, v) => receiptShell('HOTEL FOLIO / LODGING RECEIPT', p, v, [
        kv('Room', 'Jedi Suite ' + (100 + (p.seed % 40))),
        kv('Ship / arrival', p.ship),
        kv('Nights', String(1 + (p.seed % 4)))
    ])
};

const lodgingB: LabTemplateDef = {
    ...lodging,
    id: 'lodging_b',
    filename: '01_Lodging_Receipt_B.pdf',
    label: '01 Lodging Receipt B',
    build: (p) => lodging.build(p, 'b')
};

const flights: LabTemplateDef = {
    id: 'flights',
    filename: '02_Flights_Receipt.pdf',
    label: '02 Flights Receipt',
    group: 'Travel',
    variants: ['standard', 'b'],
    build: (p, v) => receiptShell('AIRLINE / CHARTER RECEIPT', p, v, [
        kv('Carrier', 'Galaxy Tours'),
        kv('Route', p.route),
        kv('Vessel', p.ship),
        kv('Passenger', p.name || '')
    ])
};

const flightsB: LabTemplateDef = {
    ...flights,
    id: 'flights_b',
    filename: '02_Flights_Receipt_B.pdf',
    label: '02 Flights Receipt B',
    build: (p) => flights.build(p, 'b')
};

const transportation: LabTemplateDef = {
    id: 'transportation',
    filename: '03_Transportation_Receipt.pdf',
    label: '03 Transportation Receipt',
    group: 'Travel',
    variants: ['standard', 'b'],
    build: (p, v) => receiptShell('TRANSPORTATION / CAR SERVICE', p, v, [
        kv('Service', 'Speeder Transfer'),
        kv('From', p.city),
        kv('To', p.planet + ' Port')
    ])
};

const transportationB: LabTemplateDef = {
    ...transportation,
    id: 'transportation_b',
    filename: '03_Transportation_Receipt_B.pdf',
    label: '03 Transportation Receipt B',
    build: (p) => transportation.build(p, 'b')
};

const travelReceipt: LabTemplateDef = {
    id: 'travel',
    filename: '03_TravelReceipt.pdf',
    label: '03 Travel Receipt',
    group: 'Travel',
    variants: ['standard'],
    build: (p, v) => receiptShell('GENERAL TRAVEL EXPENSE RECEIPT', p, v, [
        kv('Category', 'Travel'),
        kv('Reference', p.alias || '')
    ])
};

const dependentCare: LabTemplateDef = {
    id: 'dependent_care',
    filename: '04_DependentCare.pdf',
    label: '04 Dependent Care',
    group: 'Care',
    variants: ['standard'],
    build: (p) => [{
        items: [
            t('DEPENDENT CARE STATEMENT', 16, 12),
            t('Care Across the Stars Child Services', 12, 14),
            rule(),
            kv('Sponsor', p.name || ''),
            kv('Alias', p.alias || ''),
            kv('Dependent', 'Youngling ' + p.lastName),
            kv('Period start', formatDate(p.incomePayStubStartDate || '')),
            kv('Period end', formatDate(p.incomePayStubEndDate || '')),
            kv('Amount', formatMoneyPlain(p.receiptAmount || 0)),
            kv('Date', formatDate(p.receiptDate || '')),
            rule(),
            t('Provider certifies care was furnished as stated.', 10, 10)
        ]
    }]
};

const dependentCareReceipt: LabTemplateDef = {
    id: 'dependent_care_receipt',
    filename: '04_DependentCare_Receipt.pdf',
    label: '04 Dependent Care Receipt',
    group: 'Care',
    variants: ['standard', 'b'],
    build: (p, v) => receiptShell('DEPENDENT CARE RECEIPT', p, v, [
        kv('Dependent', 'Youngling ' + (p.lastName || '')),
        kv('Hours', String(4 + (p.seed % 8)))
    ])
};

const dependentCareReceiptB: LabTemplateDef = {
    ...dependentCareReceipt,
    id: 'dependent_care_receipt_b',
    filename: '04_DependentCare_Receipt_B.pdf',
    label: '04 Dependent Care Receipt B',
    build: (p) => dependentCareReceipt.build(p, 'b')
};

const dependentCompanion: LabTemplateDef = {
    id: 'dependent_companion',
    filename: '05_DependentCompanion.pdf',
    label: '05 Dependent Companion',
    group: 'Care',
    variants: ['standard'],
    build: (p) => [{
        items: [
            t('DEPENDENT COMPANION ATTESTATION', 16, 12),
            rule(),
            kv('Donor', p.name || ''),
            kv('Companion', 'Chewbacca ' + (p.lastName || 'Guard')),
            kv('Travel date', formatDate(p.receiptDate || '')),
            kv('Amount', formatMoneyPlain(p.receiptAmount || 0)),
            rule(),
            t('Companion accompanied donor for medical travel.', 10, 10)
        ]
    }]
};

const meals: LabTemplateDef = {
    id: 'meals',
    filename: '05_Meals_Receipt.pdf',
    label: '05 Meals Receipt',
    group: 'Travel',
    variants: ['standard', 'b'],
    build: (p, v) => [{
        items: [
            ...banner(v),
            t(p.merchant, 14, 6),
            t('MEAL RECEIPT', 12, 10),
            t(p.city + ' // ' + p.planet, 10, 12),
            rule(),
            t('1x Bantha Burger            12.00', 10, 8),
            t('1x Blue Milk                4.50', 10, 8),
            t('1x Ration Pack              8.25', 10, 12),
            rule(),
            kv('Guest', p.name || ''),
            kv('Amount', formatMoneyPlain(p.receiptAmount || 0)),
            kv('Date', formatDate(p.receiptDate || '')),
            t('*** CLOSED TICKET ***', 10, 10)
        ]
    }]
};

const mealsB: LabTemplateDef = {
    ...meals,
    id: 'meals_b',
    filename: '05_Meals_Receipt_B.pdf',
    label: '05 Meals Receipt B',
    build: (p) => meals.build(p, 'b')
};

const paymentAssistance: LabTemplateDef = {
    id: 'payment_assistance',
    filename: '06_PaymentAssistance.pdf',
    label: '06 Payment Assistance',
    group: 'Care',
    variants: ['standard'],
    build: (p) => [{
        items: [
            t('PAYMENT ASSISTANCE CONFIRMATION', 16, 12),
            t('Rebel Relief Fund', 12, 14),
            rule(),
            kv('Beneficiary', p.name || ''),
            kv('Alias', p.alias || ''),
            kv('Donor ID', p.donorAlias || ''),
            kv('Assistance date', formatDate(p.receiptDate || '')),
            kv('Amount', formatMoneyPlain(p.receiptAmount || 0)),
            rule(),
            t('Funds issued for approved living expenses during treatment.', 10, 10)
        ]
    }]
};

const mileage: LabTemplateDef = {
    id: 'mileage',
    filename: '06_SelfDrivingMileage_Receipt.pdf',
    label: '06 Self Driving Mileage',
    group: 'Travel',
    variants: ['standard', 'b'],
    build: (p, v) => {
        const miles = 40 + (p.seed % 120);
        return [
            {
                items: [
                    ...banner(v),
                    t('SELF-DRIVING MILEAGE LOG', 16, 12),
                    kv('Driver', p.name || ''),
                    kv('Vehicle', p.ship + ' Landspeeder'),
                    kv('Alias', p.alias || ''),
                    t('Trip detail continues on page 2.', 10, 10)
                ]
            },
            {
                items: [
                    t('Mileage detail', 14, 14),
                    kv('Stub start', formatDate(p.incomePayStubStartDate || '')),
                    kv('Stub end', formatDate(p.incomePayStubEndDate || '')),
                    kv('Route', p.route),
                    kv('Miles', String(miles)),
                    kv('Amount', formatMoneyPlain(p.receiptAmount || 0)),
                    kv('Date', formatDate(p.receiptDate || '')),
                    rule(),
                    t('Rate based on Alliance travel policy.', 10, 10)
                ]
            }
        ];
    }
};

const mileageB: LabTemplateDef = {
    ...mileage,
    id: 'mileage_b',
    filename: '06_SelfDrivingMileage_Receipt_B.pdf',
    label: '06 Self Driving Mileage B',
    build: (p) => mileage.build(p, 'b')
};

const selfEmployed: LabTemplateDef = {
    id: 'self_employed',
    filename: '07_SelfEmployedAffirmation.pdf',
    label: '07 Self Employed Affirmation',
    group: 'Income',
    variants: ['standard'],
    build: (p) => [
        {
            items: [
                t('SELF-EMPLOYMENT AFFIRMATION', 16, 12),
                rule(),
                t('I, ' + (p.name || '') + ', affirm that I am self-employed', 11, 10),
                t('as a contractor for ' + p.employer + '.', 11, 14),
                kv('Alias', p.alias || ''),
                kv('SSN', 'XXX-XX-' + ssnLast4(p)),
                kv('Date', formatDate(p.receiptDate || '')),
                rule(),
                t('Identity page. Tax figures continue on page 2 only.', 10, 10)
            ]
        },
        {
            items: [
                t('Self-employment income detail', 14, 14),
                kv('Employee name', p.name || ''),
                kv('Tax year', String(p.incomeIrsYear || '')),
                kv('IRS gross', formatMoneyPlain(p.incomeIrsYearGross || 0)),
                kv('Email', p.email || ''),
                rule(),
                t('Signature: /s/ ' + (p.name || ''), 11, 10),
                t('PAGE-2-ONLY FIELDS: tax year / IRS gross / email', 9, 8)
            ]
        }
    ]
};

const notes: LabTemplateDef = {
    id: 'notes',
    filename: '08_Notes.pdf',
    label: '08 Notes',
    group: 'Other',
    variants: ['standard'],
    build: (p) => [{
        items: [
            t('CLINICAL / OPS NOTES', 16, 12),
            rule(),
            kv('Patient', p.name || ''),
            kv('Alias', p.alias || ''),
            kv('Donor ID', p.donorAlias || ''),
            kv('Date', formatDate(p.receiptDate || '')),
            rule(),
            t('Patient reports travel from ' + p.planet + ' without incident.', 11, 10),
            t('Companion present. Follow-up scheduled after workup.', 11, 10),
            t('Ship referenced: ' + p.ship + '.', 11, 10)
        ]
    }]
};

const complications: LabTemplateDef = {
    id: 'complications',
    filename: '09_AdditionalWeeksComplicationsReport.pdf',
    label: '09 Additional Weeks Complications',
    group: 'Other',
    variants: ['standard'],
    build: (p) => [{
        items: [
            t('ADDITIONAL WEEKS / COMPLICATIONS REPORT', 14, 12),
            rule(),
            kv('Donor', p.name || ''),
            kv('Alias', p.alias || ''),
            kv('Report date', formatDate(p.receiptDate || '')),
            kv('Tax year context', String(p.incomeIrsYear || '')),
            rule(),
            t('Complication delayed return to duty on ' + p.planet + '.', 11, 10),
            t('Additional wage weeks requested under Alliance policy.', 11, 10),
            kv('Amount', formatMoneyPlain(p.receiptAmount || 0))
        ]
    }]
};

function lineItem(n: number, replacement: boolean): LabTemplateDef {
    const num = n < 10 ? '0' + n : String(n);
    return {
        id: replacement ? `line_item_${num}_rep` : `line_item_${num}`,
        filename: replacement ? `LineItemReceipt_${num}_REPLACEMENT.pdf` : `LineItemReceipt_${num}.pdf`,
        label: replacement ? `Line Item ${num} Replacement` : `Line Item ${num}`,
        group: 'Line items',
        variants: replacement ? ['replacement'] : ['standard'],
        build: (p, v) => [{
            items: [
                ...banner(replacement ? 'replacement' : v),
                t(p.merchant, 14, 8),
                t('LINE ITEM RECEIPT #' + num, 12, 12),
                rule(),
                kv('Sold to', p.name || ''),
                kv('Alias', p.alias || ''),
                kv('Item', 'Travel supply kit ' + num),
                kv('Amount', formatMoneyPlain(p.receiptAmount || 0)),
                kv('Date', formatDate(p.receiptDate || '')),
                rule(),
                t('Merchant copy - ' + p.city, 10, 10)
            ]
        }]
    };
}

export const LAB_TEMPLATES: LabTemplateDef[] = [
    payStub,
    taxDocument,
    w2Document,
    socialSecurityDocument,
    lodging,
    lodgingB,
    flights,
    flightsB,
    transportation,
    transportationB,
    travelReceipt,
    dependentCare,
    dependentCareReceipt,
    dependentCareReceiptB,
    dependentCompanion,
    meals,
    mealsB,
    paymentAssistance,
    mileage,
    mileageB,
    selfEmployed,
    notes,
    complications,
    lineItem(1, false),
    lineItem(1, true),
    lineItem(2, false),
    lineItem(2, true),
    lineItem(3, false),
    lineItem(3, true),
    lineItem(4, false),
    lineItem(5, false),
    lineItem(6, false),
    lineItem(7, false),
    lineItem(8, false)
];

export function getTemplateById(id: string): LabTemplateDef | undefined {
    for (let i = 0; i < LAB_TEMPLATES.length; i++) {
        if (LAB_TEMPLATES[i].id === id) {
            return LAB_TEMPLATES[i];
        }
    }
    return undefined;
}
